import { Fragment } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';

import ColorSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/ColorSetting';
import Padding from 'hb-builder/components/common/SettingsLibrary/CommonSettings/Padding';
import VerticalAlignment from 'hb-builder/components/common/SettingsLibrary/CommonSettings/VerticalAlignment';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';
import { isPaddingTooBig } from 'hb-builder/utils/attributes';
import type { SectionItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/sectionItemAttributes';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

const ColumnSettings = ({
  item: sectionItem,
  onChange,
  permissions: { can: { alertOnCustomPadding } } = {
    can: {},
    flags: {},
  },
}: ComponentSettingsTab<SectionItemAttributes>) => {
  const {
    features: {
      color,
      column: { canHaveBgColor },
    },
    hivebrite: { networkColors, networkTheming },
  } = useBuilderConfigContext();
  const { flagBuilderRemoveColumnBackground } = useFeatureFlag();
  const colorSettingsProps = {
    color,
    networkColors,
    networkTheming,
  };

  const canChangeBackgroundColor = flagBuilderRemoveColumnBackground
    ? canHaveBgColor
    : true;

  return (
    <>
      {sectionItem?.children?.map((item, index) => {
        const isLast = sectionItem?.children?.length === index + 1;
        const settingProps = { item, onChange };

        return (
          <Fragment key={item.id}>
            <Wrapper>
              <InputTitle
                boxProps={{ mb: 3 }}
                id={`checkboxSetting-${item.id}`}
                label={`${__('Column')} ${index + 1}`}
              />
              <VerticalAlignment {...settingProps} />
              {canChangeBackgroundColor && (
                <ColorSetting
                  settingKey="containerBackgroundColor"
                  title={__('Column background color')}
                  transparent
                  {...settingProps}
                  {...colorSettingsProps}
                />
              )}
              <Padding
                alert={{
                  enabled: alertOnCustomPadding,
                  fillConditions: (padding) => isPaddingTooBig(padding, 30),
                  message: s__(
                    'Builder|Please check if this value displays well on mobile',
                  ),
                }}
                settingKey="containerPadding"
                title={__('Column padding (in pixels)')}
                {...settingProps}
              />
            </Wrapper>
            {!isLast && (
              <Divider data-testid="column-settings-divider" mb={5} mt={2} />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default ColumnSettings;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
}))``;
